/**
 * Create User Membership Hook — envelope-returning (internalapi-envelope).
 *
 * Same conversion as `useCreateUserPass`: `mutationFn` returns the
 * `ApiResult` unchanged, invalidation is gated on `result.success`, and the
 * split callbacks keep success bodies on the bare payload while failures
 * arrive as the full envelope (`promotion_rejected` included — memberships
 * take promo codes since the 7a+m round).
 *
 * Invalidation scope: the current-club context (`useClubId`) — the same
 * source `active`/`listAll`/`all` are keyed by — rather than the response's
 * nested capitalized `Membership.clubId` (which would silently stop firing
 * when that Sequelize-shape debt is fixed; see the orchestrator). The
 * membership is always purchased at the current club.
 *
 * @module subscription/hooks
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '~/lib/query/keys';
import { useClubId } from '~/services/appRuntime';
import { membershipService } from '../api/membershipService';
import type { ApiErrorResult, ApiResult } from '~/lib/apiClient/types';
import type { CreateUserMembershipRequest, CreateUserMembershipResponse } from '../types';

export interface UseCreateUserMembershipOptions {
  /** Called when the API answered success — receives the bare payload. */
  onSuccess?: (data: CreateUserMembershipResponse, variables: CreateUserMembershipRequest) => void;

  /**
   * Called when the API answered `success: false` — the full failure envelope.
   * The expected-failure path; read `getPromotionRejection(result)` here for
   * the promo auto-drop.
   */
  onFailure?: (result: ApiErrorResult, variables: CreateUserMembershipRequest) => void;

  /** Genuine exceptions only (programmer error) — apiClient never throws. */
  onError?: (error: Error, variables: CreateUserMembershipRequest) => void;
}

export interface UseCreateUserMembershipReturn {
  /** Mutation function — resolves the ENVELOPE for every API outcome. */
  createUserMembership: (
    request: CreateUserMembershipRequest
  ) => Promise<ApiResult<CreateUserMembershipResponse>>;

  /** Whether the mutation is currently in progress */
  isCreating: boolean;

  /** Reset the mutation state */
  reset: () => void;
}

/**
 * Create a user membership (purchase).
 *
 * Note: `CreateUserMembershipRequest` does NOT include clubId — it is
 * inferred from the membership plan on the backend; invalidation uses the
 * current-club context instead (see module doc).
 */
export function useCreateUserMembership(
  options: UseCreateUserMembershipOptions
): UseCreateUserMembershipReturn {
  const { onSuccess, onFailure, onError } = options;
  const queryClient = useQueryClient();
  const [clubId] = useClubId();

  const mutation = useMutation({
    mutationFn: (request: CreateUserMembershipRequest) =>
      membershipService.createUserMembership(request),

    onSuccess: (result, variables) => {
      if (!result.success) {
        onFailure?.(result, variables);
        return;
      }

      const invalidateClubId = clubId ? Number(clubId) : undefined;

      if (invalidateClubId) {
        // Invalidate active subscription query
        queryClient.invalidateQueries({
          queryKey: queryKeys.subscriptions.active(invalidateClubId, variables.userId),
        });

        // Invalidate available subscriptions (club-level)
        queryClient.invalidateQueries({
          queryKey: queryKeys.subscriptions.all(invalidateClubId),
        });
      }

      onSuccess?.(result.payload, variables);
    },

    onError: (error, variables) => {
      onError?.(error as Error, variables);
    },
  });

  return {
    createUserMembership: mutation.mutateAsync,
    isCreating: mutation.isPending,
    reset: mutation.reset,
  };
}
